import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { createSubmission } from '../../api/runs';
import { ApiError } from '../../api/client';
import type { RunChallenge, Submission } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';

const MAX_CHARS = 5000;

export interface ChallengeFormProps {
  orgSlug: string;
  runId: string;
  challenge: RunChallenge;
  alreadySubmitted: boolean;
  onSubmitted: (submission: Submission) => void;
  /**
   * Optional submit override (issue #255 replace mode): when provided, the
   * form calls this instead of its internal createSubmission call.
   */
  submitAction?: (content: Submission['content']) => Promise<Submission>;
  /** Existing submission content to prefill the form with (replace mode). */
  initialContent?: Submission['content'];
}

const TextChallengeForm: React.FC<ChallengeFormProps> = ({
  orgSlug,
  runId,
  challenge,
  alreadySubmitted,
  onSubmitted,
  submitAction,
  initialContent,
}) => {
  const { t } = useTranslation('runs');
  const [text, setText] = useState(
    typeof initialContent?.text === 'string' ? initialContent.text : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (alreadySubmitted) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {t('participant.challenge.alreadySubmitted')}
      </Typography>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const content = { text };
      const result = submitAction
        ? await submitAction(content)
        : await createSubmission(orgSlug, runId, {
            run_challenge_id: challenge.id,
            content,
          });
      onSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && <ErrorAlert message={error} />}
        <TextField
          label={t('participant.challenge.text.label')}
          multiline
          fullWidth
          minRows={4}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
          placeholder={t('participant.challenge.text.placeholder')}
          helperText={t('participant.challenge.text.charCount', { count: text.length })}
          disabled={submitting}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!text.trim() || submitting}
        >
          {t('participant.challenge.submit')}
        </Button>
      </Stack>
    </Box>
  );
};

export default TextChallengeForm;
